import { Bento } from "@/components/ui/box";
import type { Box } from "@/components/ui/box.schema";
import type { CourseHomeProjection } from "@/lib/api/courseHome";
import { CourseInboxLink } from "@/components/inbox/CourseMessagingEntry";
import { useCourseMessagesBox } from "@/components/inbox/use-course-messages-box";
import { useTeamBox, useLearnerTeamBox, useDetailsBox, usePublicationBox, useConnectionsBox, useDriveBox } from "./course-page-boxes";

/* ------------------------------------------------------------------ */
/* The views' frame Bentos                                             */
/* ------------------------------------------------------------------ */

/** The learner's People view: who teaches the course, each with Message. */
export function CourseTeamFrame({ courseId, bandName = null }: { courseId: string; bandName?: string | null }) {
  const team = useLearnerTeamBox(courseId, bandName);
  return <Bento id="course-people" journey="course-people" boxes={[team]} />;
}

/** A teacher's People: who teaches the course, and private messages with its people (#1393 contract 4). */
export function CourseTeachingPeopleFrame({ courseId }: { courseId: string }) {
  const team = useTeamBox(courseId);
  const messages = useCourseMessagesBox(courseId);
  return <div className="grid gap-3">
    <Bento id="course-people" journey="course-people" boxes={[team, messages]} />
    <CourseInboxLink courseId={courseId} />
  </div>;
}

type SettingsProps = { courseId: string; projection: CourseHomeProjection; canEditDetails: boolean; registrar?: boolean };

/**
 * Manage course: details, publication and connections; an assigned
 * administrator also changes the team here. Keyed by course, so a check never
 * carries to another.
 */
export function CourseSettingsFrame(props: SettingsProps) {
  return <SettingsFrame key={props.courseId} {...props} />;
}

function SettingsFrame({ courseId, projection, canEditDetails, registrar = false }: SettingsProps) {
  const details = useDetailsBox({ courseId, canEditDetails });
  const publication = usePublicationBox(courseId, registrar);
  const connections = useConnectionsBox(courseId);
  const zone = projection.course.timezone ?? undefined;
  // A teacher's team is People's alone, so Manage course never reads it for them.
  return registrar
    ? <RegistrarSettings courseId={courseId} zone={zone} lead={[details, publication]} connections={connections} />
    : <Bento id="course-settings" journey="course-settings" zone={zone} boxes={[details, publication, connections]} />;
}

function RegistrarSettings({ courseId, zone, lead, connections }: { courseId: string; zone?: string; lead: Box[]; connections: Box }) {
  const team = useTeamBox(courseId, true);
  return <Bento id="course-settings" journey="course-settings" zone={zone} boxes={[...lead, team, connections]} />;
}

/**
 * Materials: the course's Drive folder, until A3's view takes it as a frame box.
 * Keyed by course, so one course's setup result never shows in another's box.
 */
export function CourseDriveFrame(props: Parameters<typeof useDriveBox>[0]) {
  return <DriveFrame key={props.courseId} {...props} />;
}

function DriveFrame(props: Parameters<typeof useDriveBox>[0]) {
  const drive = useDriveBox(props);
  if (drive.read.status === "forbidden") return null;
  return <Bento id="course-materials-frame" journey="course-materials" zone={props.timezone ?? undefined} boxes={[drive]} />;
}
